import React, { useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { paginationDropdownOptions } from 'utils/utils';

/**
 * Complete pagination component with ellipsis that replaces PrimeReact's default pagination
 * This is a single, comprehensive solution that handles everything in one place
 */
const EllipsisPagination = ({
  currentPage = 1,
  totalRecords = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  showPageSizeDropdown = true,
  isAgendaview = false,
  pageSizeOptions = paginationDropdownOptions,
}) => {
  const totalPages = Math.ceil(totalRecords / pageSize);

  const generatePageNumbers = useCallback(() => {
    const pages = [];

    // Always show at least page 1, even if totalPages is 0
    if (totalPages <= 0) {
      pages.push(1);
    } else if (totalPages <= 3) {
      // Show all pages if 3 or fewer pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // For 4+ pages, show ellipsis format
      if (currentPage <= 2) {
        // Show first 2-3 pages, ellipsis, then last page
        if (totalPages === 4) {
          pages.push(1, 2, 3, totalPages);
        } else {
          pages.push(1, 2, 3, '...', totalPages);
        }
      } else if (currentPage >= totalPages - 1) {
        // Show first page, ellipsis, then last 2-3 pages
        if (totalPages === 4) {
          pages.push(1, totalPages - 2, totalPages - 1, totalPages);
        } else {
          pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
        }
      } else {
        // Show first page, ellipsis, current page with adjacent pages, ellipsis, last page
        const showPrev = currentPage > 1;
        const showNext = currentPage < totalPages;

        pages.push(1);

        // Add ellipsis if there's a gap
        if (currentPage > 3) {
          pages.push('...');
        }

        // Add current page with adjacent pages
        if (showPrev && currentPage > 2) {
          pages.push(currentPage - 1);
        }
        pages.push(currentPage);
        if (showNext && currentPage < totalPages - 1) {
          pages.push(currentPage + 1);
        }

        // Add ellipsis if there's a gap
        if (currentPage < totalPages - 2) {
          pages.push('...');
        }

        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  const pageNumbers = useMemo(() => generatePageNumbers(), [generatePageNumbers]);

  const handlePageClick = useCallback(
    (page) => {
      if (page !== '...' && page !== currentPage && onPageChange) {
        onPageChange(page);
      }
    },
    [currentPage, onPageChange]
  );

  const handlePrevious = useCallback(() => {
    if (currentPage > 1 && onPageChange) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const handleNext = useCallback(() => {
    if (currentPage < totalPages && onPageChange) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, onPageChange]);

  const handlePageSizeChange = useCallback(
    (e) => {
      if (onPageSizeChange) {
        onPageSizeChange(e.value);
      }
    },
    [onPageSizeChange]
  );

  // Always show navigation arrows and page numbers

  // Add CSS styles for hover effects
  const paginationStyles = `
    .ellipsis-pagination .p-button {
      box-shadow: none !important;
    }
    .ellipsis-pagination .p-button:hover:not(.p-disabled):not(.p-paginator-page-selected) {
      background-color: #e9ecef !important;
    }
    .ellipsis-pagination .p-paginator-page-selected {
      background-color: transparent !important;
      border: 2px solid #517DDA !important;
      color: #6c757d !important;
    }
    .ellipsis-pagination .p-paginator-page-selected:hover {
      background-color: #f8f9fa !important;
      border: 2px solid #517DDA !important;
      color: #6c757d !important;
    }
  `;

  return (
    <div
      className="p-paginator p-component ellipsis-pagination"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0' }}
    >
      <style>{paginationStyles}</style>

      {/* Left side - Page controls and dropdown */}
      <div className="d-flex align-items-center flex-wrap justify-content-center gap-2">
        {/* Pagination Controls - always show arrows and page numbers */}
        <div className="d-flex align-items-center">
          {/* Previous Button - always show but disabled when appropriate */}
          <Button
            className={`p-paginator-prev p-paginator-element p-link ${currentPage === 1 || totalPages <= 1 ? 'p-disabled' : ''}`}
            icon="pi pi-angle-left"
            onClick={handlePrevious}
            disabled={currentPage === 1 || totalPages <= 1}
            style={{
              color: currentPage === 1 || totalPages <= 1 ? '#6c757d' : '#007bff',
              backgroundColor: 'transparent',
              border: 'none',
              padding: '0.375rem 0.5rem',
              margin: '0 0.125rem',
              cursor: currentPage === 1 || totalPages <= 1 ? 'not-allowed' : 'pointer',
              minWidth: '32px',
              height: '32px',
            }}
          />

          {/* Page Numbers with Ellipsis - always show */}
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span
                  className="p-paginator-page p-paginator-element p-link p-disabled"
                  style={{
                    color: '#6c757d',
                    padding: '0.375rem 0.5rem',
                    margin: '0 0.125rem',
                    textDecoration: 'none',
                    cursor: 'default',
                    minWidth: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    backgroundColor: 'transparent',
                  }}
                >
                  <span className="p-paginator-page-text">...</span>
                </span>
              ) : (
                <Button
                  className={`p-paginator-page p-paginator-element p-link ${page === currentPage ? 'p-paginator-page-selected' : ''}`}
                  onClick={() => handlePageClick(page)}
                  style={{
                    color: '#6c757d',
                    backgroundColor: 'transparent',
                    border: page === currentPage ? '2px solid #517DDA' : 'none',
                    padding: '0.375rem 0.5rem',
                    margin: '0 0.125rem',
                    textDecoration: 'none',
                    cursor: totalPages > 1 ? 'pointer' : 'default',
                    minWidth: '32px',
                    height: '32px',
                    borderRadius: '4px',
                  }}
                >
                  <span className="p-paginator-page-text" style={{ color: 'inherit' }}>
                    {page}
                  </span>
                </Button>
              )}
            </React.Fragment>
          ))}

          {/* Next Button - always show but disabled when appropriate */}
          <Button
            className={`p-paginator-next p-paginator-element p-link ${currentPage === totalPages || totalPages <= 1 ? 'p-disabled' : ''}`}
            icon="pi pi-angle-right"
            onClick={handleNext}
            disabled={currentPage === totalPages || totalPages <= 1}
            style={{
              color: currentPage === totalPages || totalPages <= 1 ? '#6c757d' : '#007bff',
              backgroundColor: 'transparent',
              border: 'none',
              padding: '0.375rem 0.5rem',
              margin: '0 0.125rem',
              cursor: currentPage === totalPages || totalPages <= 1 ? 'not-allowed' : 'pointer',
              minWidth: '32px',
              height: '32px',
            }}
          />
        </div>

        {/* Page Size Dropdown */}
        {showPageSizeDropdown && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: 'var(--black-500)', userSelect: 'none', fontSize: '0.875rem' }}>Show per page:</span>
            <Dropdown
              value={pageSize}
              options={isAgendaview ? pageSizeOptions : paginationDropdownOptions}
              onChange={handlePageSizeChange}
              style={{ minWidth: '80px', width: '75px' }}
            />
          </div>
        )}
      </div>

      {/* Right side - empty for now, can be used for other controls */}
      <div></div>
    </div>
  );
};

export default EllipsisPagination;
